import React from "react";
import { useDeveloperStore } from "../stores/developerStore";
import CopyButton from "./CopyButton";

// Підставляє реальну базову адресу у приклади запитів
const withBaseUrl = (text, baseUrl) => text.replace(/\{\{base_url\}\}/g, baseUrl);

// Таблиця "назва / тип / опис" для параметрів і полів відповіді
const DefinitionTable = ({ rows, firstHeader }) => (
  <table className="docs-table">
    <thead>
      <tr>
        <th>{firstHeader}</th>
        <th>Тип</th>
        <th>Опис</th>
      </tr>
    </thead>
    <tbody>
      {rows.map((row) => (
        <tr key={row.name}>
          <td>
            <code>{row.name}</code>
          </td>
          <td>{row.type}</td>
          <td className="docs-table-description">{row.description}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

// Картка одного ендпоінта: заголовок завжди видно, деталі розгортаються
const EndpointCard = ({ endpoint, baseUrl }) => (
  <details className="endpoint-card">
    <summary>
      <span className={`method-badge method-${endpoint.method.toLowerCase()}`}>
        {endpoint.method}
      </span>
      <code className="endpoint-path">{endpoint.path}</code>
      <span className="endpoint-summary">{endpoint.summary}</span>
    </summary>

    <div className="endpoint-body">
      {endpoint.params.length > 0 && (
        <>
          <h6>Параметри</h6>
          <DefinitionTable rows={endpoint.params} firstHeader="Параметр" />
        </>
      )}

      <h6>Відповідь</h6>
      <p>{endpoint.response}</p>
      {endpoint.fields.length > 0 && (
        <DefinitionTable rows={endpoint.fields} firstHeader="Поле" />
      )}

      <h6>Приклад</h6>
      <pre className="code-block">{withBaseUrl(endpoint.example.request, baseUrl)}</pre>
      <pre className="code-block">{endpoint.example.response}</pre>
    </div>
  </details>
);

// Документація read-only API: рендер JSON з бекенду + копіювання Markdown для агентів
const ApiDocs = () => {
  const { docs, copyDocsMarkdown } = useDeveloperStore();
  if (!docs) return null;

  const { base_url: baseUrl, intro, sections } = docs;

  return (
    <div className="developer-panel">
      <div className="developer-panel-header">
        <div>
          <h4>Документація API</h4>
          <p className="muted">
            Базова адреса: <code>{baseUrl}</code>. Усі ендпоінти нижче доступні за
            API-токеном, якщо роль має відповідний дозвіл.
          </p>
        </div>
        <CopyButton
          getText={copyDocsMarkdown}
          label="Скопіювати як Markdown"
          className="primary-button"
        />
      </div>

      <div className="docs-intro">
        <div className="docs-intro-block">
          <h5>Авторизація</h5>
          <ul>
            {intro.auth.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
        <div className="docs-intro-block">
          <h5>Загальні правила</h5>
          <ul>
            {intro.conventions.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
        <div className="docs-intro-block">
          <h5>Помилки</h5>
          <table className="docs-table">
            <thead>
              <tr>
                <th>Статус</th>
                <th>Опис</th>
              </tr>
            </thead>
            <tbody>
              {intro.errors.map((error) => (
                <tr key={error.status}>
                  <td>
                    <code>{error.status}</code>
                  </td>
                  <td className="docs-table-description">{error.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <nav className="docs-nav">
        {sections.map((section) => (
          <a key={section.id} href={`#docs-${section.id}`}>
            {section.title}
          </a>
        ))}
      </nav>

      {/* div, а не section: глобальний стиль задає section висоту всього екрана */}
      {sections.map((section) => (
        <div key={section.id} id={`docs-${section.id}`} className="docs-section">
          <div className="docs-section-header">
            <h5>{section.title}</h5>
            <span className="permission-badge" title="Потрібний дозвіл ролі">
              {section.permission}
            </span>
          </div>
          <p className="muted">{section.description}</p>
          {section.endpoints.map((endpoint) => (
            <EndpointCard
              key={`${endpoint.method} ${endpoint.path}`}
              endpoint={endpoint}
              baseUrl={baseUrl}
            />
          ))}
        </div>
      ))}
    </div>
  );
};

export default ApiDocs;
